/**
 * Component for handling payment processing
 * @author Housefolios
 * @ Modified by: David Helmick
 * @ Modified time: 2025-10-24 18:09:57
 */

import React from 'react'
import {
  Grid,
  Typography,
  TextField,
  InputAdornment,
  Button,
  CardContent,
  Card,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Check } from '@mui/icons-material'
import PaymentForm from '../PaymentForm'
import CountUp from 'react-countup'
// Centralized lodash imports - much cleaner!
import { capitalize } from '@/utils/lodash'

const PaymentSection = ({
  coupon,
  setCoupon,
  couponValid,
  couponPercent,
  couponAmount,
  checkCoupon,
  plan,
  token,
  createToken,
  setResetToken,
  subscriptionPlan,
  disablePromoCode,
  agree,
  handleSubmit,
  memberTrial,
  trialInvoiceAmount,
  isCoached,
  entrySubscriptionPlan,
  paymentIntervals,
  setPaymentIntervals,
  setTab,
}) => {
  const currencyFormat = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  const stripePublicKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY
  const stripePromise = loadStripe(stripePublicKey)

  return (
    <Card
      style={{
        padding: 30,
        marginLeft: 15,
        marginRight: 15,
        marginBottom: 15,
        maxWidth: 600,
      }}
    >
      <CardContent>
        <Grid container direction="column">
          <Grid style={{ marginBottom: 10 }}>
            <Grid
              container
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Grid>
                <Typography
                  variant="h5"
                  style={{
                    fontWeight: 600,
                  }}
                >
                  Billing Info
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid>
            <TextField
              label="Subscription Plan"
              value={`${capitalize(
                plan[0],
              )} Plan - ${capitalize(plan[1])}`}
              fullWidth
              variant="outlined"
              margin="dense"
              // autoFocus={true}
              disabled={true}
            />
          </Grid>
          {isCoached && (
            <Grid sx={{ my: '0.5rem' }}>
              <FormControl
                fullWidth
                variant="outlined"
                margin="dense"
              >
                <InputLabel id="payment-plans-label">
                  Payment Plans
                </InputLabel>
                <Select
                  labelId="payment-plans-label"
                  value={paymentIntervals}
                  onChange={(event) =>
                    setPaymentIntervals(Number(event.target.value))
                  }
                  label="Payment Plans"
                >
                  <MenuItem value={1}>One Payment</MenuItem>
                  <MenuItem value={2}>Two Payments</MenuItem>
                  <MenuItem value={3}>Three Payments</MenuItem>
                </Select>
              </FormControl>
              {paymentIntervals > 1 && (
                <Typography
                  style={{
                    marginLeft: 12,
                    fontSize: 13,
                  }}
                >
                  Selecting Two or Three Payments splits the coaching
                  fee into multiple monthly payments. The next months
                  payment will be the partial payment of the coaching
                  fee plus the monthly subscription amount
                </Typography>
              )}
            </Grid>
          )}
          {(!disablePromoCode || coupon) && (
            <Grid>
              <TextField
                variant="outlined"
                margin="dense"
                placeholder="Add Promo Code"
                id="input-with-icon-textfield22-1"
                value={coupon}
                fullWidth={true}
                onChange={(event) => setCoupon(event.target.value)}
                disabled={disablePromoCode}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {coupon && (
                          <Button
                            onClick={() => checkCoupon(coupon)}
                            style={{ margin: '8px' }}
                          >
                            Apply
                          </Button>
                        )}
                        {couponValid && (
                          <Check
                            style={{
                              color: 'green',
                            }}
                          />
                        )}
                        {!couponValid && (
                          <FontAwesomeIcon icon={['fas', 'xmark']} /*style={{ color: 'red' }}*/ sx={{ fontSize: "1rem !important" }} />
                        )}
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
          )}
          <Grid size={12} style={{ marginBottom: 10 }}>
            <Grid
              container
              direction="column"
              sx={{
                alignItems: 'flex-end',
              }}
            >
              {Boolean(memberTrial) ? (
                Boolean(trialInvoiceAmount) ? (
                  <Grid>
                    <Typography
                      style={
                        {
                          /* marginRight: 10, fontSize: 22, fontWeigh: 90*/
                        }
                      } /*color='primary'*/
                    >
                      {memberTrial} day trial
                    </Typography>
                  </Grid>
                ) : (
                  <Grid>
                    <Typography
                      style={
                        {
                          /* marginRight: 10, fontSize: 22, fontWeigh: 90*/
                        }
                      } /*color='primary'*/
                    >
                      {memberTrial} day free trial
                    </Typography>
                  </Grid>
                )
              ) : null}
              <Grid>
                <Grid
                  container
                  direction="row"
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Grid>
                    <Typography
                      style={{
                        marginRight: 10 /*fontSize: 22, fontWeigh: 90*/,
                      }} /*color='primary'*/
                    >
                      Total due today
                    </Typography>
                  </Grid>
                  <Grid>
                    <Typography /*style={{ fontSize: 22, fontWeigh: 900, textAlign: 'right' }} color='primary'*/
                    >
                      {Boolean(memberTrial) ? (
                        Boolean(trialInvoiceAmount) ? (
                          <CountUp
                            start={0}
                            end={trialInvoiceAmount}
                            duration={1}
                            delay={1}
                            separator=","
                            decimals={2}
                            prefix="$"
                            suffix=""
                          />
                        ) : (
                          <CountUp
                            start={0}
                            end={0}
                            duration={1}
                            delay={1}
                            separator=","
                            decimals={2}
                            prefix="$"
                            suffix=""
                          />
                        )
                      ) : isCoached ? (
                        <CountUp
                          start={0}
                          end={
                            (entrySubscriptionPlan.price *
                              (1 - couponPercent / 100) -
                              couponAmount) /
                            100
                          }
                          duration={1}
                          delay={1}
                          separator=","
                          decimals={2}
                          prefix="$"
                          suffix=""
                        />
                      ) : (
                        <CountUp
                          start={0}
                          end={
                            (subscriptionPlan.price *
                              (1 - couponPercent / 100) -
                              couponAmount) /
                            100
                          }
                          duration={1}
                          delay={1}
                          separator=","
                          decimals={2}
                          prefix="$"
                          suffix=""
                        />
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid>
                <Grid
                  container
                  direction="row"
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Grid>
                    <Typography
                      style={
                        {
                          /*marginRight: 10, fontSize: 22, fontWeigh: 90*/
                        }
                      } /*color='primary'*/
                    >
                      Next payment of{' '}
                      {currencyFormat.format(
                        isCoached
                          ? (subscriptionPlan.price +
                              (paymentIntervals > 1
                                ? entrySubscriptionPlan.price *
                                    (1 - couponPercent / 100) -
                                  couponAmount
                                : 0)) /
                              100
                          : (subscriptionPlan.price *
                              (1 - couponPercent / 100) -
                              couponAmount) /
                              100,
                      )}{' '}
                      due{' '}
                      {Boolean(memberTrial)
                        ? memberTrial + ' days'
                        : plan[1] === 'monthly' || isCoached
                          ? 'a month'
                          : 'a year'}{' '}
                      from now
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {/* {!isCoached && ( */}
              <Grid>
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={() => setTab(1)}
                  style={{ padding: 0 }}
                >
                  change
                </Button>
              </Grid>
              {/* )} */}
            </Grid>
          </Grid>
          <Grid size={12}>
            <Elements stripe={stripePromise}>
              <PaymentForm
                createToken={createToken}
                //setElement={setElement}
                setResetToken={setResetToken}
              />
            </Elements>
            <Typography
              style={{
                color: 'red',
                fontSize: 13,
                marginTop: 3,
                marginLeft: 12,
                fontWeight: 400,
              }}
              id="card-errors"
              role="alert"
            />
          </Grid>
          <Grid size={12}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              disabled={!token}
              onClick={handleSubmit}
              style={{ margin: '8px' }}
            >
              <Typography>
                Begin {capitalize(plan[0])} Plan
              </Typography>
            </Button>
          </Grid>
          {!Boolean(memberTrial) && (
            <Grid size={12} style={{ marginTop: 10 }}>
              <Typography
                style={{
                  textAlign: 'center',
                }}
              >
                30-day money-back guarantee
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  )
}

export default PaymentSection
